import Footer from "../../components/common/layout/Footer";
import ProductPlansDisplay from "@/components/features/shared/ProductPlansDisplay/ProductPlansDisplay";
import AnimatedSection from "../../components/common/animations/AnimatedSection";
import ParallaxSection from "../../components/common/animations/ParallaxSection";
import Floating3D from "../../components/common/animations/Floating3D";
import { useEffect, useState } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { motion } from "framer-motion";
import { Check, Server, Zap, Shield, Database, Code, AlertTriangle } from "lucide-react";
import { getWindowsHostingPlans } from "@/services/dreamscapeApi";
import { CurrencyCode } from "@/utils/currencyConverter";
import Breadcrumbs from "@/components/common/sections/Breadcrumbs";
import BreadcrumbsImage from "@/assets/images/domain/windowsHosting.webp";
import SEO from "@/components/common/seo/seo";

if (typeof window !== 'undefined') {
  gsap.registerPlugin(ScrollTrigger);
}

export default function WindowsHosting() {
    const [plans, setPlans] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [selectedCurrency, setSelectedCurrency] = useState<CurrencyCode>('USD');
    const [isDummyData, setIsDummyData] = useState(false);

    useEffect(() => {
        // Load Windows hosting plans from API
        const loadPlans = async () => {
            setLoading(true);
            setError(null);
            setIsDummyData(false);
            
            try {
                // Try to fetch real data from Dreamscape API (sandomains.com)
                const result = await getWindowsHostingPlans({ limit: 50 });
                
                if (result.success && result.plans && result.plans.length > 0) {
                    setPlans(result.plans);
                    setIsDummyData(false);
                } else {
                    // If API returns no plans, fall back to dummy data
                    console.warn('No plans returned from API, using dummy data:', result.message);
                    setIsDummyData(true);
                    loadDummyPlans();
                }
            } catch (err) {
                // If API call fails, fall back to dummy data
                console.error('Failed to load plans from API, using dummy data:', err);
                setError(err instanceof Error ? err.message : 'Failed to load plans');
                setIsDummyData(true);
                loadDummyPlans();
            } finally {
                setLoading(false);
            }
        };

        // Fallback dummy data function
        const loadDummyPlans = () => {
            const dummyPlans = [
                {
                    id: 1,
                    name: "Windows Starter",
                    description: "Perfect for small websites and blogs. Get started with Windows hosting at an affordable price.",
                    periods: [
                        { period: 1, price: { register: 4.99, renew: 6.99 } },
                        { period: 12, price: { register: 3.99, renew: 5.99 } },
                        { period: 24, price: { register: 3.49, renew: 5.49 } },
                        { period: 36, price: { register: 2.99, renew: 4.99 } }
                    ],
                    features: [
                        { name: "Disk Space", value: "10 GB" },
                        { name: "Bandwidth", value: "Unlimited" },
                        { name: "Email Accounts", value: "5" },
                        { name: "Databases", value: "5" },
                        { name: "ASP.NET Support", value: "Yes" }
                    ]
                },
                {
                    id: 2,
                    name: "Windows Business",
                    description: "Ideal for growing businesses. More resources and features for your Windows-based applications.",
                    periods: [
                        { period: 1, price: { register: 8.99, renew: 12.99 } },
                        { period: 12, price: { register: 7.99, renew: 11.99 } },
                        { period: 24, price: { register: 6.99, renew: 10.99 } },
                        { period: 36, price: { register: 5.99, renew: 9.99 } }
                    ],
                    features: [
                        { name: "Disk Space", value: "50 GB" },
                        { name: "Bandwidth", value: "Unlimited" },
                        { name: "Email Accounts", value: "Unlimited" },
                        { name: "Databases", value: "Unlimited" },
                        { name: "ASP.NET Support", value: "Yes" },
                        { name: "MSSQL Databases", value: "5" }
                    ]
                },
                {
                    id: 3,
                    name: "Windows Professional",
                    description: "For professional websites and applications. Maximum performance and resources.",
                    periods: [
                        { period: 1, price: { register: 14.99, renew: 19.99 } },
                        { period: 12, price: { register: 12.99, renew: 17.99 } },
                        { period: 24, price: { register: 11.99, renew: 16.99 } },
                        { period: 36, price: { register: 10.99, renew: 15.99 } }
                    ],
                    features: [
                        { name: "Disk Space", value: "Unlimited" },
                        { name: "Bandwidth", value: "Unlimited" },
                        { name: "Email Accounts", value: "Unlimited" },
                        { name: "Databases", value: "Unlimited" },
                        { name: "ASP.NET Support", value: "Yes" },
                        { name: "MSSQL Databases", value: "Unlimited" },
                        { name: "Dedicated IP", value: "1" }
                    ]
                }
            ];
            setPlans(dummyPlans);
        };

        loadPlans();
    }, []);

    useEffect(() => {
        // Animate sections on scroll
        const sections = document.querySelectorAll('.hosting-section');
        
        sections.forEach((section, index) => {
            gsap.fromTo(section,
                {
                    opacity: 0,
                    y: 60,
                    scale: 0.95
                },
                {
                    opacity: 1,
                    y: 0,
                    scale: 1,
                    duration: 0.8,
                    delay: index * 0.15,
                    ease: "power3.out",
                    scrollTrigger: {
                        trigger: section,
                        start: "top 80%",
                        toggleActions: "play none none none"
                    }
                }
            );
        });

        return () => {
            ScrollTrigger.getAll().forEach(trigger => trigger.kill());
        };
    }, [plans]);

    const handleSelectPlan = (plan: any, period: number, price: number) => {
        console.log('Selected Windows hosting plan:', plan, 'Period:', period, 'Price:', price);
        // TODO: Implement cart/checkout flow
    };

    const features = [
        {
            icon: Code,
            title: "ASP.NET Support",
            description: "Full support for ASP.NET, ASP.NET Core, and other Microsoft technologies."
        },
        {
            icon: Database,
            title: "MSSQL Database",
            description: "Microsoft SQL Server database support for your applications."
        },
        {
            icon: Server,
            title: "IIS Server",
            description: "Powered by Internet Information Services (IIS) for optimal performance."
        },
        {
            icon: Shield,
            title: "Windows Security",
            description: "Enterprise-grade Windows security features and updates."
        },
        {
            icon: Zap,
            title: "Fast Performance",
            description: "Optimized Windows servers for maximum speed and reliability."
        },
        {
            icon: Check,
            title: "24/7 Support",
            description: "Expert Windows hosting support available around the clock."
        }
    ];

    return (
        <>
            <SEO
                title="Windows Hosting - SAN micron"
                description="Experience reliable and affordable Windows web hosting with SAN micron. Enjoy fast SSD storage, IIS server support, free SSL, and 24/7 expert support to power your website effectively."
            />
            <div className="relative overflow-hidden min-h-screen text-white">
                <Breadcrumbs
                    title="Windows Hosting"
                    subtitle=""
                    breadcrumbText="Windows Hosting"
                    backgroundImage={BreadcrumbsImage}
                />
                
                {/* Hero Section */}
                <ParallaxSection speed={0.25}>
                    <Floating3D intensity={18}>
                        <section className="hosting-section container py-20 pt-32">
                            <div className="text-center max-w-4xl mx-auto">
                                {/* <h1 className="text-5xl md:text-6xl font-bold mb-6 text-transparent bg-clip-text bg-gradient-to-r from-brandStart to-brandEnd">
                                    Windows Hosting
                                </h1> */}
                                <p className="text-xl text-textColor mb-8 leading-relaxed">
                                    Get the best value for your Windows web-hosting with all the latest features 
                                    you need on your website runtime and speed first.
                                </p>
                            </div>
                        </section>
                    </Floating3D>
                </ParallaxSection>

                {/* Pricing Plans */}
                <AnimatedSection delay={0.2} direction="up">
                    <div className="hosting-section">
                        {isDummyData ? (
                            <div className="container py-12">
                                <div className="max-w-2xl mx-auto">
                                    <div className="bg-yellow-500/10 border-2 border-yellow-500/50 rounded-lg p-6 flex items-start gap-4">
                                        <AlertTriangle className="w-6 h-6 text-yellow-500 flex-shrink-0 mt-0.5" />
                                        <div className="flex-1">
                                            <h3 className="text-yellow-400 font-bold text-lg mb-2">
                                                Using Fallback Data
                                            </h3>
                                            <p className="text-yellow-300/90 text-sm leading-relaxed">
                                                Unable to load plans from the API. Displaying sample data. Please check your API configuration or contact support to ensure real hosting plan data is available from sandomains.com.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ) : null}
                        <ProductPlansDisplay
                            plans={plans}
                            loading={loading}
                            error={error}
                            currency={selectedCurrency}
                            onCurrencyChange={setSelectedCurrency}
                            onSelectPlan={handleSelectPlan}
                            buttonText="Get Started"
                            priceLabel="per month"
                        />
                    </div>
                </AnimatedSection>

                {/* Features Section */}
                <ParallaxSection speed={0.3} direction="down">
                    <AnimatedSection delay={0.3} direction="left">
                        <section className="hosting-section container py-20">
                            <div className="text-center mb-16">
                                <h2 className="text-4xl font-bold mb-4">Why Choose Windows Hosting?</h2>
                                <p className="text-textColor text-lg">Perfect for Microsoft-based applications</p>
                            </div>
                            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                                {features.map((feature, index) => {
                                    const Icon = feature.icon;
                                    return (
                                        <motion.div
                                            key={index}
                                            className="bg-cardBg/90 backdrop-blur-xl rounded-2xl p-6 border border-white/10 hover:border-primary/30 transition-all"
                                            whileHover={{ scale: 1.02, y: -5 }}
                                            transition={{ type: "spring", stiffness: 300 }}
                                        >
                                            <div className="w-12 h-12 rounded-full bg-gradient-to-r from-brandStart to-brandEnd flex items-center justify-center mb-4">
                                                <Icon className="w-6 h-6 text-white" />
                                            </div>
                                            <h3 className="text-xl font-bold mb-2">{feature.title}</h3>
                                            <p className="text-textColor">{feature.description}</p>
                                        </motion.div>
                                    );
                                })}
                            </div>
                        </section>
                    </AnimatedSection>
                </ParallaxSection>

                {/* FAQ Section */}
                {/* <AnimatedSection delay={0.6}>
                    <div className="abstract-bg hosting-section">
                    </div>
                </AnimatedSection> */}

                <AnimatedSection delay={0.7}>
                    <Footer />
                </AnimatedSection>
            </div>
        </>
    );
}

